'use client';

import { useState, useEffect } from 'react';
import OrderStatusTimeline from './OrderStatusTimeline';
import OrderDetailsCard from './OrderDetailsCard';
import DeliveryPersonCard from './DeliveryPersonCard';
import LiveTrackingMap from './LiveTrackingMap';
import QRCodeVerification from './QRCodeVerification';
import SupportActions from './SupportActions';
import FeedbackPrompt from './FeedbackPrompt';
import RelatedOrders from './RelatedOrders';

interface OrderTrackingInteractiveProps {
  orderId: string;
}

const OrderTrackingInteractive = ({ orderId }: OrderTrackingInteractiveProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentStage, setCurrentStage] = useState(2);
  const [isDelivered, setIsDelivered] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const orderItems = [
  {
    id: '1',
    name: 'Fresh Salmon Fillet',
    quantity: 2,
    weight: '500g',
    price: 499.98,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1017a97cd-1765873722883.png",
    alt: 'Fresh pink salmon fillet on white plate with lemon slices and herbs'
  },
  {
    id: '2',
    name: 'Tiger Prawns',
    quantity: 1,
    weight: '250g',
    price: 349.99,
    image: "https://images.unsplash.com/photo-1665262734761-065d7c2b1382",
    alt: 'Large tiger prawns arranged on ice with garnish'
  }];


  const deliveryPerson = {
    name: 'Rajesh Kumar',
    phone: '+91 98765 43210',
    vehicleNumber: 'MH 02 AB 1234',
    rating: 4.8,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_181b06d86-1764786944721.png",
    alt: 'Smiling delivery person in blue uniform with helmet'
  };

  const relatedOrders = [
  {
    id: '1',
    orderNumber: 'FB2025001',
    date: '20 Dec 2025',
    status: 'Delivered',
    total: 649.99,
    itemCount: 3,
    thumbnail: "https://images.unsplash.com/photo-1592180293405-4a3ba4e3d3c9",
    alt: 'Assorted sushi platter with salmon, tuna, and avocado rolls'
  },
  {
    id: '2',
    orderNumber: 'FB2025002',
    date: '15 Dec 2025',
    status: 'Delivered',
    total: 899.99,
    itemCount: 4,
    thumbnail: "https://images.unsplash.com/photo-1618862633131-289f00c6a09e",
    alt: 'Fresh tuna steaks on wooden cutting board with spices'
  }];


  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background pt-[120px] pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-muted rounded w-1/4"></div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <div className="h-64 bg-muted rounded-lg"></div>
                <div className="h-96 bg-muted rounded-lg"></div>
              </div>
              <div className="space-y-6">
                <div className="h-48 bg-muted rounded-lg"></div>
                <div className="h-64 bg-muted rounded-lg"></div>
              </div>
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="space-y-6">
      {/* Order Status Timeline */}
      <OrderStatusTimeline orderId={orderId} currentStage={currentStage} />

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Primary Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Live Tracking Map */}
          <LiveTrackingMap
            deliveryLat={19.076}
            deliveryLng={72.8777}
            destinationAddress="123, Marine Drive, Mumbai, Maharashtra 400020" />


          {/* Order Details */}
          <OrderDetailsCard
            orderId={orderId}
            orderDate="25 Dec 2025, 10:30 AM"
            items={orderItems}
            subtotal={849.97}
            deliveryFee={50.00}
            total={899.97}
            deliveryAddress="123, Marine Drive, Mumbai, Maharashtra 400020"
            estimatedDelivery="Today, 4:00 PM - 5:00 PM" />


          {/* Feedback Prompt (Mobile) */}
          <div className="lg:hidden">
            <FeedbackPrompt orderId={orderId} isDelivered={isDelivered} />
          </div>

          {/* Related Orders (Mobile) */}
          <div className="lg:hidden">
            <RelatedOrders orders={relatedOrders} />
          </div>
        </div>

        {/* Right Column - Secondary Information */}
        <div className="space-y-6">
          {/* Delivery Person Card */}
          <DeliveryPersonCard deliveryPerson={deliveryPerson} />

          {/* QR Code Verification */}
          <QRCodeVerification orderId={orderId} batchNumber="FB-BATCH-2025-1234" />

          {/* Support Actions */}
          <SupportActions orderId={orderId} canModify={!isDelivered} />

          {/* Feedback Prompt (Desktop) */}
          <div className="hidden lg:block">
            <FeedbackPrompt orderId={orderId} isDelivered={isDelivered} />
          </div>

          {/* Related Orders (Desktop) */}
          <div className="hidden lg:block">
            <RelatedOrders orders={relatedOrders} />
          </div>
        </div>
      </div>
    </div>);

};

export default OrderTrackingInteractive;